/**
 * Bottom HUD while navigating: ETA / remaining, speed readout, recenter and
 * end buttons, plus transient status toasts (rerouting, offline).
 */

import type { NavProgress } from '../core/types';
import { formatDistance, formatDuration, formatEta, formatSpeed, speedUnitLabel } from '../core/format';
import type { UnitSystem } from '../core/settings';
import { t } from '../core/i18n';

export class NavHud {
  readonly el: HTMLElement;
  private toastTimer: number | null = null;

  constructor(
    parent: HTMLElement,
    private actions: { onEnd: () => void; onRecenter: () => void; onOverview: () => void },
  ) {
    this.el = document.createElement('div');
    this.el.className = 'hud';
    this.el.hidden = true;
    this.el.innerHTML = `
      <div class="hud__toast" hidden></div>
      <button class="hud__recenter icon-btn" hidden aria-label="Re-centre">
        <svg viewBox="0 0 24 24"><path d="M12 3 L19 20 L12 16 L5 20 Z" fill="currentColor"/></svg>
        <span>${t('nav.recenter')}</span>
      </button>
      <div class="hud__bar">
        <button class="hud__end" aria-label="End navigation">
          <svg viewBox="0 0 24 24"><path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"/></svg>
        </button>
        <div class="hud__stats" role="button" tabindex="0" aria-label="Route overview">
          <div class="hud__eta">--:--</div>
          <div class="hud__sub"><span class="hud__dist">--</span> · <span class="hud__dur">--</span></div>
        </div>
        <div class="hud__speed">
          <div class="hud__speed-value">--</div>
          <div class="hud__speed-unit">km/h</div>
        </div>
      </div>`;
    parent.appendChild(this.el);

    this.el.querySelector('.hud__end')!.addEventListener('click', () => this.actions.onEnd());
    this.el.querySelector('.hud__recenter')!.addEventListener('click', () => this.actions.onRecenter());
    this.el.querySelector('.hud__stats')!.addEventListener('click', () => this.actions.onOverview());
  }

  show(): void {
    this.el.hidden = false;
  }

  hide(): void {
    this.el.hidden = true;
    this.setRecenterVisible(false);
  }

  update(progress: NavProgress, speedMs: number | null, units: UnitSystem, speedWarn: boolean): void {
    (this.el.querySelector('.hud__eta') as HTMLElement).textContent = formatEta(progress.durationRemaining);
    (this.el.querySelector('.hud__dist') as HTMLElement).textContent = formatDistance(progress.distanceRemaining, units);
    (this.el.querySelector('.hud__dur') as HTMLElement).textContent = formatDuration(progress.durationRemaining);
    (this.el.querySelector('.hud__speed-value') as HTMLElement).textContent = formatSpeed(speedMs, units);
    (this.el.querySelector('.hud__speed-unit') as HTMLElement).textContent = speedUnitLabel(units);
    this.el.querySelector('.hud__speed')!.classList.toggle('is-over', speedWarn);
  }

  setRecenterVisible(visible: boolean): void {
    (this.el.querySelector('.hud__recenter') as HTMLElement).hidden = !visible;
  }

  toast(message: string, sticky = false): void {
    const t = this.el.querySelector('.hud__toast') as HTMLElement;
    t.textContent = message;
    t.hidden = false;
    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
    if (!sticky) {
      this.toastTimer = window.setTimeout(() => (t.hidden = true), 4000);
    }
  }

  clearToast(): void {
    (this.el.querySelector('.hud__toast') as HTMLElement).hidden = true;
    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
  }
}
